import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  pagosEmpleadosAPI,
  empleadosAPI,
  formasPagoAPI,
  bancosAPI,
  cuentasBancariasAPI,
  movimientosPredefinidosAPI,
  withSaldoNegativoConfirm,
} from '../services/api';
import { toast } from 'react-toastify';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import FormasPagoEditor from '../components/FormasPagoEditor';
import { camposDolar } from '../utils/montoDolarFormaPago';
import '../Clientes.css';

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const esChequeTipoPropio = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('cheque propio') || n.includes('cheque de tercero');
};

const EmpleadoPagoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fechaHoy } = useZonaHoraria();

  const [empleado, setEmpleado] = useState(null);
  const [formData, setFormData] = useState({ monto: '', fecha: '', concepto: '' });
  const [formasPagoPago, setFormasPagoPago] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [predefinidoId, setPredefinidoId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, fecha: prev.fecha || fechaHoy() }));
  }, [fechaHoy]);

  const loadEmpleado = useCallback(async () => {
    if (!id) return;
    try {
      const res = await empleadosAPI.getById(id);
      if (res.success) setEmpleado(res.data);
    } catch {
      toast.error('Error al cargar empleado');
    }
  }, [id]);

  const loadCatalogos = useCallback(async () => {
    try {
      const [resFp, resBancos, resCb, resPre] = await Promise.all([
        formasPagoAPI.getDisponibles({ tipo_movimiento: 'egreso' }),
        bancosAPI.getAll({ activo: true }),
        cuentasBancariasAPI.getAll({ activo: true }),
        movimientosPredefinidosAPI.getAll({ activo: true, uso: 'empleados' }),
      ]);
      if (resFp.success) setFormasPago(resFp.data || []);
      if (resBancos.success) setBancos(resBancos.data || []);
      if (resCb.success) setCuentasBancarias(resCb.data || []);
      if (resPre.success) {
        const pre = (resPre.data || []).find(
          (m) => (m.descripcion || '').trim().toLowerCase().includes('pago')
        );
        if (pre) setPredefinidoId(pre.id);
      }
    } catch {
      toast.error('No se pudieron cargar los datos iniciales');
    }
  }, []);

  useEffect(() => {
    loadEmpleado();
    loadCatalogos();
  }, [loadEmpleado, loadCatalogos]);

  const formasPagoHabilitadas = useMemo(
    () =>
      formasPago.filter((f) => {
        const tm = (f.tipo_movimiento || '').toLowerCase();
        return tm === 'egreso' || tm === 'ambos';
      }),
    [formasPago]
  );

  const construirFormasPagoPayload = () =>
    formasPagoPago.map((fp) => {
      const formaPagoObj = formasPago.find((f) => String(f.id) === String(fp.forma_pago_id));
      const nombre = formaPagoObj?.nombre?.toLowerCase() || '';
      const resultado = {
        forma_pago_id: fp.forma_pago_id,
        forma_pago_nombre: formaPagoObj?.nombre,
        monto: parseFloat(fp.monto),
        ...camposDolar(fp, { formasPago, cuentasBancarias, modoEgreso: true }),
      };
      if (esChequeTipoPropio(nombre)) {
        const esTercero = nombre.includes('cheque de tercero');
        resultado.cheque_propio = {
          cuenta_bancaria_id: esTercero ? null : (fp.cheque_propio?.cuenta_bancaria_id || null),
          banco_id: esTercero ? (fp.cheque_propio?.banco_id || null) : null,
          numero: fp.cheque_propio?.numero,
          fecha_emision: fp.cheque_propio?.fecha_emision || formData.fecha,
          fecha_cobro: fp.cheque_propio?.fecha_cobro || null,
          importe: parseFloat(fp.monto),
          es_electronico: Boolean(fp.cheque_propio?.es_electronico),
        };
      } else if (nombre.includes('cheque')) {
        resultado.cheque = {
          banco_id: fp.cheque?.banco_id,
          numero_cheque: fp.cheque?.numero_cheque,
          importe: parseFloat(fp.monto),
          fecha_emision: fp.cheque?.fecha_emision,
          fecha_cobro: fp.cheque?.fecha_cobro || null,
          titular: fp.cheque?.titular,
        };
      } else if (nombre.includes('transferencia')) {
        const tr = fp.transferencia || {};
        resultado.transferencia = {
          cuenta_bancaria_id: tr.cuenta_bancaria_id ?? formaPagoObj?.cuenta_bancaria_id ?? null,
          fecha: tr.fecha || formData.fecha,
          monto: parseFloat(tr.monto != null && tr.monto !== '' ? tr.monto : fp.monto),
        };
      } else if (nombre.includes('tarjeta')) {
        resultado.tarjeta = { numero_ticket: fp.tarjeta?.numero_ticket };
      }
      return resultado;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const monto = parseFloat(formData.monto || 0);
    if (!formData.fecha) return toast.error('Ingrese la fecha');
    if (!formData.concepto.trim()) return toast.error('Ingrese el concepto');
    if (isNaN(monto) || monto <= 0) return toast.error('Ingrese un monto mayor a 0');
    if (formasPagoPago.length === 0) return toast.error('Agregue al menos una forma de pago');
    const totalFp = formasPagoPago.reduce((s, fp) => s + parseFloat(fp.monto || 0), 0);
    if (Math.abs(totalFp - monto) > 0.01) {
      return toast.error(`La suma de formas de pago (${formatMoneda(totalFp)}) debe ser igual al monto (${formatMoneda(monto)})`);
    }

    setLoading(true);
    try {
      const res = await withSaldoNegativoConfirm((confirmar) =>
        pagosEmpleadosAPI.crear({
          empleado_id: Number(id),
          monto,
          fecha: formData.fecha,
          concepto: formData.concepto.trim(),
          movimiento_predefinido_id: predefinidoId,
          formas_pago: construirFormasPagoPayload(),
          ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
        })
      );
      if (res.success) {
        toast.success(`Pago N° ${res.data?.numero_pago || ''} registrado`);
        navigate(`/empleados/${id}/movimientos`);
      } else {
        toast.error(res.message || 'No se pudo registrar el pago');
      }
    } catch (err) {
      if (!err.cancelled) toast.error(err.message || 'No se pudo registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Pago a empleado</h1>
        {empleado && <p style={{ color: '#666', marginTop: 4 }}>{empleado.nombre}</p>}
      </div>

      <form onSubmit={handleSubmit} className="cliente-form" style={{ maxWidth: '900px' }}>
        <div className="form-row">
          <div className="form-group">
            <label>Fecha *</label>
            <input
              type="date"
              value={formData.fecha}
              onChange={(e) => setFormData((p) => ({ ...p, fecha: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>Monto *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monto}
              onChange={(e) => setFormData((p) => ({ ...p, monto: e.target.value }))}
              required
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Concepto *</label>
          <input
            type="text"
            value={formData.concepto}
            onChange={(e) => setFormData((p) => ({ ...p, concepto: e.target.value }))}
            required
            placeholder="Concepto del pago"
          />
        </div>

        <FormasPagoEditor
          value={formasPagoPago}
          onChange={setFormasPagoPago}
          formasPago={formasPagoHabilitadas}
          bancos={bancos}
          setBancos={setBancos}
          cuentasBancarias={cuentasBancarias}
          fecha={formData.fecha}
          fechaHoy={fechaHoy}
          montoTotal={formData.monto}
        />

        <div className="form-actions">
          <Link to={`/empleados/${id}/movimientos`} className="btn-secondary">Cancelar</Link>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Registrar pago'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmpleadoPagoForm;
